import {
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { MatExpansionPanel } from '@angular/material/expansion'
import {
  ApiService,
  AuthService,
  Clinician,
  ConfirmService,
  Countries,
  defaultQuestions,
  GuarantorRelationships,
  InsuranceRelationship,
  Intake,
  IntakeModel,
  IntakeSection,
  IntakeService,
  isHiddenSection,
  MatProgressButtonOptions,
  PatientIntakeFormData,
  ProgressIndicatorService,
  QuestionBase,
  Questions,
  QuestionType,
  QuestionWithChildren,
  Sexes,
  States,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { each, extend, filter, find, isEqual, map, size, uniq } from 'lodash'
import moment from 'moment'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { IntakeField } from '../intake-field'

@Directive({
  standalone: false,
})
export class BasePartialDirective implements OnDestroy {
  @ViewChild(MatExpansionPanel) panel?: MatExpansionPanel
  @Input() section: IntakeSection
  @Input() customQuestions: any[] = []
  @Output() intakeField = new EventEmitter<IntakeField>()

  intake: IntakeModel | null
  intakeData: PatientIntakeFormData
  clinician: Clinician
  clinicians: Clinician[]
  questionsBase: QuestionBase<any>[]
  QuestionType = QuestionType
  questions: Questions
  sexes = Sexes
  relationships = InsuranceRelationship
  guarantorRelationships = GuarantorRelationships
  countries = Countries
  states = States
  form: UntypedFormGroup
  self: any
  errors = false
  recordValues: any
  /**
   * Indicates that the intakeData has been updated
   * and the section should be refreshed
   * which is actually handled in the `CustomSectionComponent` which extends this class
   */
  shouldRefresh = false

  // Button Options
  btnOpts: MatProgressButtonOptions = {
    active: false,
    text: 'common.save',
    buttonColor: 'primary',
    barColor: 'primary',
    matType: 'mat-raised-button',
    mode: 'indeterminate',
    value: 0,
    disabled: false,
  }

  private onDestroy$: Subject<void> = new Subject<void>()

  constructor(
    private authService: AuthService,
    private intakeService: IntakeService,
    private restService: ApiService,
    private fb: UntypedFormBuilder,
    private progressService: ProgressIndicatorService,
    private confirmService: ConfirmService,
    private translateService: TranslateService,
  ) {
    this.intake = this.intakeService.intake
    this.intakeData = this.intakeService.data
    this.clinicians = this.intakeService.clinicians

    this.authService.changes
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((user) => {
        this.clinician = user
      })

    this.intakeService.onIntakeChange
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((intake) => {
        this.intake = intake
        this.intakeData.fromJson(intake)
      })

    this.intakeService.onFileUpload
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(async (questionId) => {
        if (questionId) {
          const question = find(this.questions, (q) => q.id === questionId)
          if (
            question &&
            (this.intake?.isConfirmed ||
              this.intake?.stage === Intake.STAGE.TECH_REVIEW)
          ) {
            this.intakeData.recentlyUploadedFiles.push(questionId)
            this.intakeData.recentlyUploadedFiles = uniq(
              this.intakeData.recentlyUploadedFiles,
            )
            this.updateRecentlyModifiedSections()
            await this.saveIntake(true)
          }
        }
      })
    this.intakeService.onSaveAll
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(async (res) => {
        if (res) {
          await this.submit(true)
        }
      })
  }

  ngOnDestroy(): void {
    this.intakeService.onSaveAll.next(false)
    this.intakeService.onIntakeChange.next(null)
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  get clinicianName(): string | void {
    if (this.clinicians && this.intake?.confirmed) {
      const clinician = this.clinicians.find(
        (c) => c.id === this.intake?.confirmed?.clinicianId,
      )
      if (clinician) {
        return clinician.name
      } else {
        return 'someone'
      }
    }
  }

  formatDate(date: moment.MomentInput, format: string = 'YYYY-MM-DD') {
    return date ? moment(date).format(format) : ''
  }

  buildForm(customQuestions: any, section?: string) {
    if (section) {
      this.questions = defaultQuestions[section](customQuestions, true)
    } else {
      this.questions = customQuestions
    }
    this.form = this.toFormGroup(this.questions)
    this.recordValues = this.form.value
  }

  toFormGroup(qs: QuestionBase<any>[]) {
    this.questionsBase = qs
    const fields = this.convertQuestionsToFormControls(qs)
    return this.fb.group(fields)
  }

  async submit(savingAll?: boolean) {
    this.btnOpts.active = true
    if (savingAll) {
      await this.saveState(this.questions)
      await this.intakeService.onIntakeChange.next(
        new IntakeModel(this.intakeData),
      )
      this.panel.close()
      this.btnOpts.active = false
    } else if (this.form.valid) {
      // check if clinician edit a pending intake status
      if (this.intake?.stage === Intake.STAGE.PENDING) {
        this.confirmService
          .confirm({
            message: this.translateService.instant(
              'patient-intake.intake-still-pending-text',
            ),
          })
          .subscribe(async (confirmed) => {
            if (confirmed) {
              this.intakeData.stage = Intake.STAGE.TECH_REVIEW
              await this.saveIntake()
            } else {
              this.btnOpts.active = false
            }
          })
      } else if (!this.intake?.isNotConfirmed) {
        this.showIntakeSignedDialog()
      } else {
        await this.saveIntake()
      }
    } else {
      // Show required fields error message
      this.displayErrorMessages()

      this.confirmService
        .confirm({
          message: this.translateService.instant(
            'patient-intake.required-questions',
          ),
          html: true,
        })
        .subscribe(async (confirmed) => {
          if (confirmed) {
            if (!this.intake?.isNotConfirmed) {
              this.showIntakeSignedDialog()
            } else {
              await this.saveIntake()
            }
          } else {
            this.btnOpts.active = false
          }
        })
    }
  }
  private showIntakeSignedDialog() {
    this.confirmService
      .confirm({
        message: this.translateService.instant(
          this.clinicianName
            ? 'patient-intake.intake-already-signed'
            : 'patient-intake.intake-already-confirmed',
          { value: this.clinicianName },
        ),
      })
      .subscribe(async (confirmed) => {
        if (confirmed) {
          this.intakeData.stage = Intake.STAGE.TECH_REVIEW
          await this.saveIntake()
        } else {
          this.btnOpts.active = false
        }
      })
  }
  private convertQuestionsToFormControls(questions: QuestionBase<any>[]) {
    const formControls = {}

    questions.forEach((question) => {
      question.valueFromData(this.intakeData)
      const controls = question.toFormControls(this.fb)
      extend(formControls, controls)

      if (
        (question as QuestionWithChildren).ifQuestions &&
        size((question as QuestionWithChildren).ifQuestions) > 0
      ) {
        const ifControls = this.convertQuestionsToFormControls(
          (question as QuestionWithChildren).ifQuestions,
        )
        extend(formControls, ifControls)
      }
    })

    return formControls
  }
  private displayErrorMessages() {
    this.errors = true

    if (this.form) {
      each(this.questions, (question) => {
        this.markQuestionsAsDirty(question)
      })
    }
  }

  private markQuestionsAsDirty(question: QuestionBase<any>) {
    if (
      question.control &&
      (question.control.controls['value'] as UntypedFormGroup).controls
    ) {
      const controls = (question.control.controls['value'] as UntypedFormGroup)
        .controls
      each(controls, (questionControl) => {
        questionControl.markAsTouched()
        questionControl.markAsDirty()
        questionControl.updateValueAndValidity()
      })
    } else if (question.control && question.control.controls['value']) {
      question.control.controls['value'].markAsTouched()
      question.control.controls['value'].markAsDirty()
      question.control.controls['value'].updateValueAndValidity()
    } else {
      question.control.markAsTouched()
      question.control.markAsDirty()
      question.control.updateValueAndValidity()
    }

    if ((question as QuestionWithChildren).ifQuestions) {
      each((question as QuestionWithChildren).ifQuestions, (q) => {
        this.markQuestionsAsDirty(q)
      })
    }
  }

  private async saveIntake(open?: boolean) {
    // save all form data to the intake data object
    await this.saveState(this.questions)
    this.saveCurrentSections()
    return this.restService.updateIntake(this.intakeData).subscribe(
      (intake) => {
        // Refresh form
        this.intakeService.onIntakeChange.next(intake)
        this.shouldRefresh = true
        // this.form = this.toFormGroup(this.questionsBase)
        this.progressService.snack(
          this.translateService.instant('patient-intake.intake-updated'),
        )
        if (!open) {
          this.panel.close()
        }
        this.btnOpts.active = false
      },
      () => {
        this.progressService.snack(
          this.translateService.instant('patient-intake.intake-updated'),
        )
        this.btnOpts.active = false
      },
    )
  }

  private async saveState(qs: Questions) {
    const saveQuestionValuesToData = function (questions: Questions) {
      questions.forEach((question) => {
        question.saveValueToData(this.intakeData, this.form, this.clinician)
        const questionWithChildren = question as QuestionWithChildren
        if (
          questionWithChildren.ifQuestions &&
          questionWithChildren.ifQuestions.length > 0
        ) {
          saveQuestionValuesToData(questionWithChildren.ifQuestions)
        }
      })
    }.bind(this)

    // Check if section was recently modified
    if (
      !isEqual(this.form.value, this.recordValues) &&
      (this.intake?.isConfirmed ||
        this.intake?.stage === Intake.STAGE.TECH_REVIEW)
    ) {
      this.updateRecentlyModifiedSections()
    }
    saveQuestionValuesToData(qs)
  }

  private updateRecentlyModifiedSections() {
    const sectionFound = find(this.intakeService.sections, {
      sectionId: this.section.sectionId,
    })
    this.intakeData.recentlyModifiedSections.push(sectionFound.path)
    this.intakeData.recentlyModifiedSections = uniq(
      this.intakeData.recentlyModifiedSections,
    )
  }

  private saveCurrentSections() {
    const sections = filter(this.intakeService.sections, (section) => {
      return !isHiddenSection(
        section.requirements,
        new IntakeModel(this.intakeData),
      )
    })
    this.intakeData.sections = map(sections, (section) => section.path)
  }
}
